/**
 * Cross-window softphone sync.
 *
 * The widget runs in its own window, which is its own JS context with its own Vuex store. Only that
 * window holds the WebRTC transport (the OWNER); every dashboard tab is a read-only MIRROR that
 * renders what the owner broadcasts and asks the owner to act on its behalf.
 *
 * That split is what makes the campaigns list correct: a mirror never guesses at call state, so it
 * cannot show Go Live enabled on one row while the agent is already on a call for another.
 */
export const SOFTPHONE_STATE_CHANNEL = 'ringbax-softphone-state'

export const SOFTPHONE_MESSAGES = Object.freeze({
  // owner -> mirrors, on every state transition
  STATE: 'state',
  // mirror -> owner, "I just opened, tell me where things stand"
  HELLO: 'hello',
  // mirror -> owner, an action the mirror is not allowed to perform itself
  COMMAND: 'command',
  // owner -> mirrors, the widget window is going away
  CLOSED: 'closed'
})

export const SOFTPHONE_COMMANDS = Object.freeze({
  GO_ONLINE: 'goOnline',
  GO_OFFLINE: 'goOffline',
  // The wrap-up controls, for the same reason Go Offline is here: the countdown runs in the owner,
  // so a mirror asks it to end or extend rather than moving a timer it does not hold.
  FINISH_WRAP_UP: 'finishWrapUp',
  EXTEND_WRAP_UP: 'extendWrapUp'
})

const NO_OP = () => {}

/**
 * A channel with no BroadcastChannel behind it. Safari <15.4 and some embedded webviews have none;
 * degrading to silence keeps the widget itself working (it is the owner and needs no one's
 * agreement) and costs only the dashboard's live button states.
 */
function createNoOpChannel() {
  return {
    isSupported: false,
    postState: NO_OP,
    postHello: NO_OP,
    postCommand: NO_OP,
    postClosed: NO_OP,
    onState: NO_OP,
    onHello: NO_OP,
    onCommand: NO_OP,
    onClosed: NO_OP,
    close: NO_OP
  }
}

export function createSoftphoneChannel(channelName = SOFTPHONE_STATE_CHANNEL) {
  if (typeof BroadcastChannel === 'undefined') {
    return createNoOpChannel()
  }

  const channel = new BroadcastChannel(channelName)
  const handlers = {
    [SOFTPHONE_MESSAGES.STATE]: [],
    [SOFTPHONE_MESSAGES.HELLO]: [],
    [SOFTPHONE_MESSAGES.COMMAND]: [],
    [SOFTPHONE_MESSAGES.CLOSED]: []
  }

  channel.onmessage = event => {
    const message = event?.data

    if (!message || !handlers[message.type]) {
      return
    }

    handlers[message.type].forEach(handler => handler(message))
  }

  const post = message => {
    try {
      channel.postMessage(message)
    } catch (error) {
      // A closed channel throws. Nothing useful to do: the sender is either unloading or already
      // torn down, and the receiver will fall back to its own state.
      console.error('Failed to post a softphone message', error)
    }
  }

  return {
    isSupported: true,

    postState(snapshot) {
      post({ type: SOFTPHONE_MESSAGES.STATE, snapshot })
    },

    postHello() {
      post({ type: SOFTPHONE_MESSAGES.HELLO })
    },

    postCommand(name, payload = null) {
      post({ type: SOFTPHONE_MESSAGES.COMMAND, name, payload })
    },

    postClosed() {
      post({ type: SOFTPHONE_MESSAGES.CLOSED })
    },

    onState(handler) {
      handlers[SOFTPHONE_MESSAGES.STATE].push(handler)
    },

    onHello(handler) {
      handlers[SOFTPHONE_MESSAGES.HELLO].push(handler)
    },

    onCommand(handler) {
      handlers[SOFTPHONE_MESSAGES.COMMAND].push(handler)
    },

    onClosed(handler) {
      handlers[SOFTPHONE_MESSAGES.CLOSED].push(handler)
    },

    close() {
      channel.onmessage = null
      channel.close()
    }
  }
}

export default createSoftphoneChannel
